import React from "react";
import { CalendarCheck, CalendarRange, ClipboardList, IndianRupee, Users } from "lucide-react";
import { ProgressBar, StatsCard, StatsGrid } from "../../ui";
import { formatCurrency, formatDate } from "../../../utils/format";
import { attendanceTone, batchTimeline, seatUsage } from "./batchDetails";

/**
 * A tile's icon carries colour only when its number needs attention — five
 * tiles in five hues say nothing, and then the red one says nothing either.
 * Keys are batchDetails tones; values are StatsCard's tone names.
 */
const ICON_TONE = { success: "emerald", warning: "amber", danger: "rose" };

/** The line under a figure, and the meter that draws the same number. */
const Measure = ({ children, value, tone = "primary" }) => (
  <>
    <span className="tw-block">{children}</span>
    {value !== null && value !== undefined && (
      <ProgressBar value={value} color={tone} size="sm" className="tw-mt-2" />
    )}
  </>
);

const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

function timelineLine(batch, timeline) {
  switch (timeline.phase) {
    case "before": return `Starts in ${plural(timeline.daysToStart, "day")}`;
    case "running": return `${plural(timeline.daysLeft, "day")} left · ends ${formatDate(batch.endDate)}`;
    case "after": return `Ended ${formatDate(batch.endDate)}`;
    default: return "No dates set";
  }
}

/**
 * BatchVitals — the numbers an admin reads first: how full the batch is,
 * whether students turn up, what is owed, how far along it is, and how much
 * work has been set.
 *
 * Props:
 *   batch           — the `GET /batches/:id` batch
 *   stats           — the `/batches/:id/stats` resource
 *   assignmentCount — the assignments resource (a number)
 *   showFees        — false when the plan or the caller has no fee access
 */
const BatchVitals = ({ batch, stats, assignmentCount, showFees }) => {
  const statsLoading = stats.loading && !stats.data;
  const s = stats.data;
  const seats = seatUsage(batch, s);
  const timeline = batchTimeline(batch);
  const held = s?.totalSessions || 0;
  const attendance = held ? attendanceTone(s.avgAttendance) : null;
  const total = Number(s?.totalFees) || 0;
  const paid = Number(s?.paidFees) || 0;
  const full = seats.cap !== null && seats.used >= seats.cap;

  return (
    <StatsGrid cols={showFees ? 5 : 4}>
      <StatsCard
        title="Active students"
        icon={Users}
        iconColor={full ? "amber" : "primary"}
        loading={statsLoading}
        value={seats.used}
        subtitle={seats.cap ? (
          <Measure value={seats.percent} tone={full ? "warning" : "primary"}>
            {full ? `Full · ${seats.cap} seats` : `of ${seats.cap} seats`}
          </Measure>
        ) : (
          <Measure>No seat limit</Measure>
        )}
      />

      <StatsCard
        title="Attendance"
        icon={CalendarCheck}
        iconColor={attendance === "success" ? "primary" : ICON_TONE[attendance] || "primary"}
        loading={statsLoading}
        value={held ? `${s.avgAttendance}%` : "—"}
        subtitle={held ? (
          <Measure value={s.avgAttendance} tone={attendance}>
            Across {plural(held, "class", "classes")}
          </Measure>
        ) : (
          <Measure>No classes held yet</Measure>
        )}
      />

      {showFees && (
        <StatsCard
          title="Fees pending"
          icon={IndianRupee}
          iconColor="primary"
          loading={statsLoading}
          value={total > 0 ? formatCurrency(s.pendingFees) : "—"}
          subtitle={total > 0 ? (
            <Measure value={Math.round((paid / total) * 100)} tone="success">
              {formatCurrency(paid)} of {formatCurrency(total)} paid
            </Measure>
          ) : (
            <Measure>No fees set</Measure>
          )}
        />
      )}

      <StatsCard
        title="Timeline"
        icon={CalendarRange}
        iconColor="primary"
        value={timeline.phase === "unscheduled" ? "—" : `${timeline.percent}%`}
        subtitle={(
          <Measure value={timeline.phase === "unscheduled" ? null : timeline.percent}>
            {timelineLine(batch, timeline)}
          </Measure>
        )}
      />

      <StatsCard
        title="Assignments"
        icon={ClipboardList}
        iconColor="primary"
        loading={assignmentCount.loading && assignmentCount.data === null}
        value={assignmentCount.data ?? "—"}
        subtitle={<Measure>Set for this batch</Measure>}
      />
    </StatsGrid>
  );
};

export default BatchVitals;
